import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid
} from "recharts";
import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";

const Reports = ({ alerts }) => {
  const currentYear = new Date().getFullYear();

  // ✅ Calculate alert counts by type
  const { criticalCount, warningCount, infoCount } = useMemo(() => {
    let critical = 0,
      warning = 0,
      info = 0;
    alerts.forEach((a) => {
      if (a.type === "Critical") critical++;
      else if (a.type === "Warning") warning++;
      else if (a.type === "Info") info++;
    });
    return { criticalCount: critical, warningCount: warning, infoCount: info };
  }, [alerts]);

  // ✅ Group alerts by month for Line Chart
  const monthlyData = useMemo(() => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const data = months.map((m, index) => ({
      month: `${m} '${currentYear.toString().slice(-2)}`,
      Alerts: 0,
      Critical: 0,
      Warning: 0,
      Info: 0
    }));

    alerts.forEach((a) => {
      const date = new Date(a.datetime);
      if (date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        data[monthIndex].Alerts += 1;
        if (a.type === "Critical") data[monthIndex].Critical += 1;
        else if (a.type === "Warning") data[monthIndex].Warning += 1;
        else if (a.type === "Info") data[monthIndex].Info += 1;
      }
    });

    // ✅ Remove future months beyond the current month
    const currentMonth = new Date().getMonth();
    return data.slice(0, currentMonth + 1);
  }, [alerts, currentYear]);

    // ✅ PieChart Data
  const pieData = [
    { name: "Critical", value: criticalCount },
    { name: "Warning", value: warningCount },
    { name: "Info", value: infoCount },
  ];

  const COLORS = ["#EF4444", "#FACC15", "#3B82F6"];

    // ✅ LineChart Data (Alerts trend by last 10 alerts)
  const lineData = useMemo(() => {
    return alerts.slice(-10).map((a, i) => ({
      index: i + 1,
      Critical: a.type === "Critical" ? 1 : 0,
      Warning: a.type === "Warning" ? 1 : 0,
      Info: a.type === "Info" ? 1 : 0,
    }));
  }, [alerts]);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Reports & Analytics</h1>

        {/* Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Critical Alerts */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-md flex items-center gap-4">
          <div className="p-4 bg-red-600 rounded-full">
            <FaExclamationTriangle className="text-white text-2xl" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Critical Alerts</p>
            <p className="text-3xl font-bold text-white">{criticalCount}</p>
          </div>
        </div>

        {/* Warning Alerts */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-md flex items-center gap-4">
          <div className="p-4 bg-yellow-500 rounded-full">
            <FaExclamationTriangle className="text-white text-2xl" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Warning Alerts</p>
            <p className="text-3xl font-bold text-white">{warningCount}</p>
          </div>
        </div>

        {/* Info Alerts */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-md flex items-center gap-4">
          <div className="p-4 bg-blue-400 rounded-full">
            <FaInfoCircle className="text-white text-2xl" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Info Alerts</p>
            <p className="text-3xl font-bold text-white">{infoCount}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-md">
          <h3 className="text-lg font-bold mb-4">Alert Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Line Chart */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-md">
          <h3 className="text-lg font-bold mb-4">Recent Alerts Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ReLineChart data={lineData}>
              <XAxis dataKey="index" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Critical" stroke="#EF4444" />
              <Line type="monotone" dataKey="Warning" stroke="#FACC15" />
              <Line type="monotone" dataKey="Info" stroke="#3B82F6" />
            </ReLineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* ✅ Full-Width Monthly Alerts Line Chart */}
      <div className="bg-gray-800 p-6 rounded-xl shadow-md mt-6">
        <h3 className="text-lg font-bold mb-4">Monthly Alerts Trend (Jan - Current Month)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ReLineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="month" stroke="#ccc" />
            <YAxis stroke="#ccc" />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Alerts" stroke="#10B981" strokeWidth={2} name="Total Alerts" />
            <Line type="monotone" dataKey="Critical" stroke="#EF4444" />
            <Line type="monotone" dataKey="Warning" stroke="#FACC15" />
            <Line type="monotone" dataKey="Info" stroke="#3B82F6" />
          </ReLineChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
};

export default Reports;
